import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Dropdown, Modal, Spin } from "antd";
import { SubscriptionContext } from "context/SubscriptionProvider";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { usePreconModuleBodyClass } from "hooks/use-precon-module-body-class";
import CatalogItemsView from "./catalog-items-view";
import ImportDesignScopeDrawer from "./import-design-scope-drawer";
import { useBootstrapDesignScopeApis } from "./use-bootstrap-design-scope-apis";

export type BootstrapDesignScopeRow = {
  id: string;
  title: string;
  description: string;
  itemsCount?: number;
  addedBy?: string;
  addedOn?: string;
};

function TitleCellRenderer({
  data,
  context
}: ICellRendererParams<BootstrapDesignScopeRow>) {
  if (!data) return null;
  const onOpenCatalog = (
    context as { onOpenCatalog?: (catalogId: string) => void } | undefined
  )?.onOpenCatalog;
  return (
    <button
      type="button"
      className="m-0 cursor-pointer border-0 bg-transparent p-0 text-[length:inherit] font-medium text-ciq-black-a98 underline decoration-dotted decoration-1 underline-offset-[3px] hover:text-ciq-black-a98 hover:underline hover:decoration-dotted"
      data-testid={`bootstrap-ds-title-${data.id}`}
      onClick={() => onOpenCatalog?.(data.id)}
    >
      {data.title}
    </button>
  );
}

function ActionsCellRenderer({
  data,
  context
}: ICellRendererParams<BootstrapDesignScopeRow>) {
  if (!data) return null;
  const onOpenCatalog = (
    context as { onOpenCatalog?: (catalogId: string) => void } | undefined
  )?.onOpenCatalog;
  const onDeleteCatalog = (
    context as
      | { onDeleteCatalog?: (row: BootstrapDesignScopeRow) => void }
      | undefined
  )?.onDeleteCatalog;
  return (
    <div className="flex w-full items-center justify-end">
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{
          items: [
            { key: "edit", label: "Edit List", icon: <EditOutlined /> },
            {
              key: "delete",
              label: "Delete List",
              icon: <DeleteOutlined />,
              className:
                "!text-ciq-red-700 hover:!bg-black/[0.04] hover:!text-ciq-red-800"
            }
          ],
          onClick: ({ key }) => {
            if (key === "edit") onOpenCatalog?.(data.id);
            if (key === "delete") onDeleteCatalog?.(data);
          }
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<MoreOutlined />}
          aria-label={`Actions for ${data.title}`}
          data-testid={`bootstrap-ds-actions-${data.id}`}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    </div>
  );
}

function BootstrapDesignScopeSettings() {
  usePreconModuleBodyClass(true);
  const gridRef = useRef<AgGridReact<BootstrapDesignScopeRow>>(null);
  const { eventLogs = [] } = useContext(SubscriptionContext);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(
    null
  );
  const [catalogPendingDelete, setCatalogPendingDelete] =
    useState<BootstrapDesignScopeRow | null>(null);
  const [deletingCatalog, setDeletingCatalog] = useState(false);
  const {
    catalogs,
    loadingCatalogs,
    loadCatalogs,
    updateCatalog,
    deleteCatalog
  } = useBootstrapDesignScopeApis();

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      const shouldReloadCatalogs = eventLogs.some(
        (log) =>
          log.data_source === "feature_catalog" ||
          (log.data_source === "feature_instance" &&
            log.change_type === "import")
      );
      if (shouldReloadCatalogs) {
        loadCatalogs({ silent: true });
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, loadCatalogs]);

  const selectedCatalog = useMemo(
    () => catalogs.find((catalog) => catalog.id === selectedCatalogId) ?? null,
    [catalogs, selectedCatalogId]
  );

  const openCatalog = useCallback((catalogId: string) => {
    setSelectedCatalogId(catalogId);
  }, []);

  const requestDeleteCatalog = useCallback((row: BootstrapDesignScopeRow) => {
    setCatalogPendingDelete(row);
  }, []);

  const confirmDeleteCatalog = useCallback(async () => {
    if (!catalogPendingDelete || deletingCatalog) return;
    setDeletingCatalog(true);
    try {
      const deleted = await deleteCatalog(catalogPendingDelete.id);
      if (deleted) setCatalogPendingDelete(null);
    } finally {
      setDeletingCatalog(false);
    }
  }, [catalogPendingDelete, deleteCatalog, deletingCatalog]);

  const rowData = useMemo<BootstrapDesignScopeRow[]>(
    () =>
      catalogs.map((catalog) => ({
        id: catalog.id,
        title: catalog.name,
        description: catalog.description || "",
        itemsCount: catalog.itemCount ?? undefined,
        addedBy: catalog.addedBy ?? undefined,
        addedOn: catalog.addedOn ?? undefined
      })),
    [catalogs]
  );

  const columnDefs = useMemo<ColDef<BootstrapDesignScopeRow>[]>(
    () => [
      {
        headerName: "Title",
        field: "title",
        colId: "title",
        flex: 1,
        minWidth: 160,
        maxWidth: 240,
        cellRenderer: TitleCellRenderer,
        suppressHeaderMenuButton: true
      },
      {
        headerName: "Description",
        field: "description",
        colId: "description",
        flex: 3,
        minWidth: 280,
        wrapText: true,
        autoHeight: true,
        cellClass: "!items-start !whitespace-normal !py-3.5 !leading-[1.45]",
        suppressHeaderMenuButton: true
      },
      {
        headerName: "No of Items",
        field: "itemsCount",
        colId: "itemsCount",
        width: 120,
        minWidth: 100,
        flex: 0,
        suppressHeaderMenuButton: true
      },
      {
        headerName: "Added By",
        field: "addedBy",
        colId: "addedBy",
        flex: 1,
        minWidth: 140,
        maxWidth: 220,
        suppressHeaderMenuButton: true
      },
      {
        headerName: "Added On",
        field: "addedOn",
        colId: "addedOn",
        flex: 1,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) =>
          params.value
            ? DateUtils.format(params.value, DATE_FORMAT_MMDDYYYY)
            : "",
        suppressHeaderMenuButton: true
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 88,
        minWidth: 72,
        flex: 0,
        sortable: false,
        resizable: false,
        pinned: "right",
        lockPosition: "right",
        suppressMovable: true,
        headerClass: "[&_.ag-header-cell-label]:!justify-end",
        cellClass: "ciq-cell-v-center",
        cellRenderer: ActionsCellRenderer,
        suppressHeaderMenuButton: true
      }
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      menuTabs: [],
      cellClass: "ciq-cell-v-center"
    }),
    []
  );

  const getRowId = useCallback(
    (params: { data: BootstrapDesignScopeRow }) => params.data.id,
    []
  );

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-ciq-white"
      data-testid="bootstrap-design-scope"
    >
      {selectedCatalog ? (
        <CatalogItemsView
          key={selectedCatalog.id}
          catalog={selectedCatalog}
          onBack={() => setSelectedCatalogId(null)}
          onCatalogUpdated={(patch) => updateCatalog(selectedCatalog.id, patch)}
        />
      ) : (
        <>
          <div className="flex shrink-0 items-start justify-between px-8 pt-6">
            <div>
              <div className="flex items-center gap-2">
                <div className="text-xl font-bold text-ciq-gray-950">
                  Design Scope List
                </div>
                <span
                  className="inline-flex min-w-[22px] items-center justify-center rounded-full bg-ciq-gray-100 px-1.5 text-xs text-ciq-gray-800"
                  data-testid="bootstrap-ds-count"
                >
                  {rowData.length}
                </span>
              </div>
              <div className="mt-1 text-sm text-ciq-gray-800">
                Import reusable scope lists to quickly set up projects.
              </div>
            </div>
            <Button
              type="primary"
              className="h-8 px-4 bg-ciq-primary-800 border-ciq-primary-800"
              data-testid="bootstrap-ds-import-csv"
              onClick={() => setImportDrawerOpen(true)}
            >
              Import CSV
            </Button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-6">
            <Spin
              spinning={loadingCatalogs}
              description="Loading"
              classNames={{
                root: "block h-full min-h-0 [&_.ant-spin-container]:h-full"
              }}
            >
              <div
                className="ag-theme-alpine h-full min-h-0 [--ag-border-color:var(--ciq-gray-200)] [--ag-cell-horizontal-padding:16px] [--ag-header-background-color:var(--ciq-white)] [--ag-header-foreground-color:var(--ciq-black-a87)] [--ag-row-border-color:var(--ciq-gray-200)] [&_.ag-header-cell-label]:font-bold [&_.ag-cell]:leading-[1.4]"
                data-testid="bootstrap-design-scope-grid"
              >
                <AgGridReact<BootstrapDesignScopeRow>
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={getRowId}
                  headerHeight={40}
                  animateRows={false}
                  suppressDragLeaveHidesColumns
                  suppressContextMenu
                  suppressCellFocus
                  suppressNoRowsOverlay={loadingCatalogs}
                  overlayNoRowsTemplate="No lists found."
                  context={{
                    onOpenCatalog: openCatalog,
                    onDeleteCatalog: requestDeleteCatalog
                  }}
                />
              </div>
            </Spin>
          </div>

          <ImportDesignScopeDrawer
            open={importDrawerOpen}
            existingListNames={catalogs.map((catalog) => catalog.name)}
            onClose={() => setImportDrawerOpen(false)}
            onImported={() => {
              loadCatalogs();
            }}
          />
          <Modal
            open={catalogPendingDelete != null}
            title="Delete this list?"
            okText="Delete"
            cancelText="Cancel"
            closable={!deletingCatalog}
            mask={{ closable: !deletingCatalog }}
            keyboard={!deletingCatalog}
            okButtonProps={{
              danger: true,
              className: "ciq-delete-btn",
              loading: deletingCatalog,
              "data-testid": "bootstrap-ds-delete-confirm"
            }}
            cancelButtonProps={{
              disabled: deletingCatalog,
              "data-testid": "bootstrap-ds-delete-cancel"
            }}
            onOk={() => {
              confirmDeleteCatalog();
            }}
            onCancel={() => {
              if (deletingCatalog) return;
              setCatalogPendingDelete(null);
            }}
          >
            <p
              className="m-0 text-ciq-gray-950"
              data-testid="bootstrap-ds-delete-message"
            >
              “{catalogPendingDelete?.title}” will no longer be available to
              bootstrap future projects.
            </p>
          </Modal>
        </>
      )}
    </div>
  );
}

export default BootstrapDesignScopeSettings;
